import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

export interface ReportVisibilityEntry {
  reportId: string;
  role: string;
}

/**
 * Report ids hidden for the CURRENT user at a location. Used to filter report
 * cards. Default is visible, so an empty list means everything shows.
 */
export function useEffectiveReportHidden(locationId: string | null | undefined) {
  return useQuery({
    queryKey: ['pos', 'report-visibility', 'effective', locationId],
    queryFn: () => api.get<{ data: { hiddenReportIds: string[] } }>(`/pos/report-visibility/effective/${locationId}`),
    enabled: !!locationId,
  });
}

/** Full hidden matrix for a location — admin config UI only. */
export function useReportVisibilityMatrix(locationId: string | null | undefined) {
  return useQuery({
    queryKey: ['pos', 'report-visibility', 'matrix', locationId],
    queryFn: () => api.get<{ data: { hidden: ReportVisibilityEntry[] } }>(`/pos/report-visibility/${locationId}`),
    enabled: !!locationId,
  });
}

/** Replace the hidden set for a location. */
export function useUpdateReportVisibility() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { locationId: string; hidden: ReportVisibilityEntry[] }) =>
      api.put<{ data: { locationId: string; hidden: ReportVisibilityEntry[] } }>('/pos/report-visibility', data),
    onSuccess: (_res, variables) => {
      qc.invalidateQueries({ queryKey: ['pos', 'report-visibility', 'matrix', variables.locationId] });
      qc.invalidateQueries({ queryKey: ['pos', 'report-visibility', 'effective', variables.locationId] });
    },
  });
}
